import { noop } from 'rxjs';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

import { ApplicationScreen } from 'src/app/application-navigator/application-screen';
import { ConfirmationDialog } from 'src/app/common/confirmation-dialog/confirmation-dialog.component';
import { LoadingIndicator } from 'src/app/acumatica/loading-indicator/AcumaticaLoadingOptions';
import { CustomerIDSelectorComponent } from 'src/app/common/customer-id-selector/customer-id-selector.component';
import { Customer, CustomerAddress, CustomerLocation, CustomerSalesStatusList, CustomerSampleBook, CustomerSerivce } from './../../acumatica/EntityServices/Customer.service';
import { AppConfig } from 'src/app/app-config.service';

@Component({
  selector: 'app-customer-info',
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.css']
})
export class CustomerInfoComponent extends ApplicationScreen implements OnInit, AfterViewInit {
  pageTitle = new FormControl('Customer Info');
  customer: Customer | null = null;
  customerLocation: CustomerLocation | null = null;
  currentBookNote: string | null = null;
  confirmedRemovingBooks: boolean = false;

  priceLists = [
    { type: "all", label: "All Products" },
    { type: "dra", label: "Drapery & Privacy Curtains" },
    { type: "lea", label: "Leather" },
    { type: "pan", label: "Panel" },
    { type: "uph", label: "Upholstery" },
  ];
  pricing = AppConfig.settings.CustomerPricing;
  today = new Date();

  @ViewChild('customerSelector') customerSelector?: CustomerIDSelectorComponent;

  private _customerID?: string;
  get customerID(): string | undefined { return this._customerID; }
  set customerID(value: string | undefined) {
    this._customerID = value;
  }
  customerSelected(customer: Customer | null) {
    this.customer = customer;
    this.customerLocation = this.customer?.Locations?.find(loc => loc.Default) || null;

    if (customer) {
      if (customer.CustomerID != this.paramCustomerID) {
        this.router.navigate(['/customer-info', customer?.CustomerID]);
      }
      else {
        setTimeout(() => {
          this.pageTitle.setValue(customer?.CustomerIDName);
        });
      }
    } else {
      this.router.navigate(['/customer-info']);
    }
  }

  get displayLocation(): CustomerAddress | null {
    return this.customerLocation || this.customer;
  }
  get displayLocationMapQuery() {
    let address = this.displayLocation;
    if (!address) return null;
    return address.AddressLine1 + ',' +
      (address.AddressLine2 ? address.AddressLine2 + ', ' : '') +
      address.City + ', ' + address.State + ' ' + (address.PostalCode || '')
  }
  get hasDS(): boolean {
    return !!this.customer?.Locations?.find(loc => loc.Active && loc.LocationID.toUpperCase() == 'DS');
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private customers: CustomerSerivce,
  ) {
    super();
  }

  private paramCustomerID: string | null = null;
  ngOnInit(): void {
    this.route.paramMap
      .subscribe(params => {
        this.paramCustomerID = params.get('customerID');
        if (this.paramCustomerID) {
          this.customerID = this.paramCustomerID;
        }
      });
  }

  ngAfterViewInit(): void {
    if (!this.customerSelector?.customerID) this.customerSelector?.focus();
  }

  public get CustomerSalesStatusList() { return CustomerSalesStatusList; }

  removeBook(book: CustomerSampleBook, panel: MatExpansionPanel) {
    // define callback function to do the removal
    let removeBook = (confirmed: boolean) => {
      if (!confirmed) return;

      book.__delete = true;
      this.updateBook(book);

      this.confirmedRemovingBooks = true;
      panel.disabled = true;
      panel.close();
    }

    // user has already confirmed removal -- just do it
    if (this.confirmedRemovingBooks) removeBook(true)
    else {
      // get confirmation to do removal
      this.dialog.open(ConfirmationDialog, {
        data: {
          message: "Remove this sample book from the customer?\n" + book.Description,
          confirmText: 'Yes, remove it',
          cancelText: 'Cancel'
        }
      }).afterClosed().subscribe(removeBook)
    }
  }

  focusBookNotes(book: CustomerSampleBook) { this.currentBookNote = book.Notes; }
  updateBookNotes(book: CustomerSampleBook) { if (book.Notes !== this.currentBookNote) this.updateBook(book); }

  updateBook(book: CustomerSampleBook) {
    let updateCustomer = new Customer({ CustomerID: this.customer?.CustomerID });
    updateCustomer.SampleBooks = [book];
    this.customers.put(updateCustomer, { loader: { message: "Updating Acumatica (" + book.InventoryID + ")", indicator: LoadingIndicator.snackbar } })
      .subscribe(() => { });
  }
  
  updateNextApptDate(event: MatDatepickerInputEvent<Date>) {
    this.updateCustomerData(new Customer({ CustomerID: this.customer!.CustomerID!, NextAppt: event.value }));
  }

  updateSalesStatus(event: MatSelectChange) {
    this.updateCustomerData(new Customer({ CustomerID: this.customer!.CustomerID!, SalesStatus: event.value }));
  }

  updateRepNotes(event: FocusEvent) {
    this.updateCustomerData(new Customer({ CustomerID: this.customer!.CustomerID!, RepNotes: (<any>event.target).value }));
  }

  private updateCustomerData(customer: Customer) {
    this.customers.put(customer, { loader: { message: "Updating Customer in Acumatica", indicator: LoadingIndicator.snackbar } })
    .subscribe(noop);  
  }
}